import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PricingPlans } from "@/components/PricingPlans";
import { PublicNavMobile } from "@/components/PublicNavMobile";
import { ZOOD_LOGO, ZOOD_MASCOT } from "@/lib/brand";
import {
  Sparkles, ArrowRight, LogIn, BookOpen, PenLine, Calculator, FlaskConical, Languages,
  Hand, Volume2, MousePointerClick, Beaker, Star, Trophy, Zap, ShieldCheck,
  Heart, Users, GraduationCap, Check, MessagesSquare, Globe, HandHeart,
} from "lucide-react";

const SUBJECTS = [
  { icon: BookOpen, name: "Reading & Phonics", color: "#0284C7", tint: "#E0F2FE", desc: "Sound out words, blend and read with meaning." },
  { icon: Sparkles, name: "Vocabulary", color: "#7A3FF2", tint: "#F3E8FF", desc: "Grow word power with matching and meanings." },
  { icon: PenLine, name: "Writing & Cursive", color: "#0D9488", tint: "#E0E7FF", desc: "Trace letters, words and sentences by hand." },
  { icon: Calculator, name: "Math", color: "#FF9E1B", tint: "#FFEDD5", desc: "Count, add and solve with tap-along objects." },
  { icon: FlaskConical, name: "Science", color: "#10B981", tint: "#D1FAE5", desc: "Predict, test and explore real experiments." },
  { icon: Languages, name: "Foreign Language", color: "#DB2777", tint: "#FCE7F3", desc: "First words and phrases in Spanish and French." },
  { icon: MessagesSquare, name: "Social Communication", color: "#0EA5E9", tint: "#E0F2FE", desc: "Greetings, listening and taking turns in conversation." },
  { icon: Globe, name: "Social Studies", color: "#B45309", tint: "#FEF3C7", desc: "Family, community helpers, maps and then-and-now." },
  { icon: Heart, name: "Social-Emotional Learning", color: "#E11D48", tint: "#FFE4E6", desc: "Name feelings, cool down and be a good friend." },
  { icon: HandHeart, name: "Life Skills", color: "#65A30D", tint: "#ECFCCB", desc: "Healthy habits, safe choices and responsibility." },
];

const ENGINES = [
  { icon: Hand, title: "Handwriting Tracer", text: "Trace strokes with finger, stylus or mouse — scored in real time.", color: "#0D9488" },
  { icon: MousePointerClick, title: "Tap-to-Count Math", text: "Kids tap and combine objects to build number sense.", color: "#FF9E1B" },
  { icon: Volume2, title: "Phonics Read-Aloud", text: "Every word and sound speaks — powered by natural audio.", color: "#0284C7" },
  { icon: Beaker, title: "Sink-or-Float Lab", text: "Make a prediction, drop it in the water, see what happens.", color: "#10B981" },
];

const REWARDS = [
  { icon: Zap, title: "Points", text: "Earn points for every lesson finished." },
  { icon: Star, title: "Subject Stickers", text: "Collect a sticker for completing each subject." },
  { icon: Trophy, title: "Badges & Streaks", text: "Unlock badges that keep kids coming back." },
];

const GRADES = ["PreK", "K", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th"];

function Nav() {
  const linkClass = "block rounded-2xl px-4 py-3 text-base font-semibold text-[#1F2937] hover:bg-[#F2ECE1]";
  const links = [
    ["#subjects", "Subjects"], ["#engines", "Interactive"], ["#rewards", "Rewards"],
    ["#pricing", "Pricing"], ["/earn", "Earn With Us"], ["/help", "How it works"],
    ["/login", "Log In"],
  ];
  const items = links.map(([href, label]) => ({
    label, key: href,
    node: href.startsWith("/")
      ? <Link to={href} className={linkClass}>{label}</Link>
      : <a href={href} className={linkClass}>{label}</a>,
  }));
  return (
    <header className="sticky top-0 z-40 border-b border-[#E5DECF] bg-[#FAF7F2]/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <a href="#top" className="flex items-center gap-2.5">
          <img src={ZOOD_LOGO} alt="ZoodLeap" className="h-9 w-auto sm:h-10" data-testid="landing-logo" />
        </a>
        <nav className="hidden items-center gap-1 md:flex">
          {[["#subjects", "Subjects"], ["#engines", "Interactive"], ["#rewards", "Rewards"], ["#pricing", "Pricing"], ["/earn", "Earn With Us"]].map(([href, label]) => (
            href.startsWith("/")
              ? <Link key={href} to={href} className="rounded-full px-4 py-2 text-sm font-semibold text-[#1F2937] hover:bg-[#F2ECE1]">{label}</Link>
              : <a key={href} href={href} className="rounded-full px-4 py-2 text-sm font-semibold text-[#1F2937] hover:bg-[#F2ECE1]">{label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/help" data-testid="nav-help-link" className="hidden rounded-full px-4 py-2 text-sm font-bold text-[#1F2937] hover:bg-[#F2ECE1] sm:inline-flex">How it works</Link>
          <Link to="/login" data-testid="nav-login-link" className="hidden rounded-full px-4 py-2 text-sm font-bold text-[#1F2937] hover:bg-[#F2ECE1] sm:inline-flex">Log In</Link>
          <Link to="/signup" data-testid="nav-signup-link" className="hidden rounded-full bg-[#0D9488] px-4 py-2 text-sm font-bold text-white shadow-sm transition-all hover:bg-[#0B7F74] active:translate-y-0.5 md:inline-flex">Start Free</Link>
          <PublicNavMobile
            items={items}
            cta={<Link to="/signup" className="block rounded-2xl bg-[#0D9488] px-5 py-3 text-center font-heading text-base font-bold text-white shadow-sm">Start Free</Link>}
          />
        </div>
      </div>
    </header>
  );
}

export default function Landing() {
  return (
    <div id="top" className="min-h-screen bg-[#FAF7F2] text-[#1F2937]">
      <Nav />

      {/* Hero */}
      <section className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:py-20 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#F59E0B]/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#B45309]">
            <Sparkles size={14} /> The complete PreK–8th learning platform
          </div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Leap into learning with <span className="text-[#17BEBB]">Zood</span><span className="text-[#0D9488]">Leap</span>.
          </h1>
          <p className="mt-5 font-body text-base sm:text-lg text-[#6B7280] max-w-xl">
            Reading, phonics, vocabulary, handwriting, math, science, foreign language, social studies, social-emotional learning and life skills — all in one playful, interactive world for ages 5–14.
            Start free, then unlock the full PreK–8th curriculum.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {GRADES.map((g) => (
              <span key={g} className="rounded-full border border-[#E5DECF] bg-white px-3 py-1 text-xs font-bold text-[#6B7280]">{g}</span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/signup" data-testid="hero-start-free" className="inline-flex items-center gap-2 rounded-2xl bg-[#0D9488] px-6 py-4 text-base font-bold text-white shadow-md transition-all hover:bg-[#0B7F74] hover:shadow-lg active:translate-y-0.5">
              Start Free <ArrowRight size={18} />
            </Link>
            <Link to="/student-login" data-testid="hero-student-login" className="inline-flex items-center gap-2 rounded-2xl bg-white border-2 border-[#E5DECF] px-6 py-4 text-base font-bold text-[#1F2937] transition-all hover:border-[#0D9488] active:translate-y-0.5">
              <LogIn size={18} /> I'm a Student
            </Link>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
          className="relative flex items-center justify-center">
          <div className="absolute inset-0 -z-0 mx-auto h-[85%] w-[85%] self-center rounded-full bg-gradient-to-br from-[#17BEBB]/20 via-[#FFD84D]/20 to-[#7A3FF2]/20 blur-3xl" />
          <motion.img
            src={ZOOD_MASCOT} alt="Zoodle the ZoodLeap mascot" data-testid="hero-mascot"
            className="relative z-10 w-[78%] max-w-[440px] drop-shadow-[0_20px_40px_rgba(23,190,187,0.25)]"
            animate={{ y: [0, -14, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
          <div className="absolute -bottom-2 -left-2 z-20 hidden rounded-2xl border border-[#E5DECF] bg-white px-4 py-3 shadow-lg sm:flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#10B981] text-white"><Star size={18} /></div>
            <div className="leading-tight"><div className="font-heading text-sm font-bold">Sticker earned!</div><div className="font-body text-xs text-[#6B7280]">Science Explorer</div></div>
          </div>
          <div className="absolute -top-1 right-0 z-20 hidden rounded-2xl border border-[#E5DECF] bg-white px-4 py-3 shadow-lg sm:flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FF9E1B] text-white"><Zap size={18} /></div>
            <div className="leading-tight"><div className="font-heading text-sm font-bold">+50 points</div><div className="font-body text-xs text-[#6B7280]">Lesson complete!</div></div>
          </div>
        </motion.div>
      </section>

      {/* Subjects */}
      <section id="subjects" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Ten subjects, one academy</div>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Everything they need to grow</h2>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SUBJECTS.map((s, i) => (
            <motion.div key={s.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              whileHover={{ y: -4 }} data-testid={`subject-showcase-${i}`}
              className="rounded-3xl border border-[#E5DECF] bg-white p-6" style={{ boxShadow: "0 8px 30px rgb(59,66,159,0.05)" }}>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl text-white" style={{ background: s.color }}><s.icon size={28} /></div>
              <h3 className="mt-4 font-heading text-xl font-bold">{s.name}</h3>
              <p className="mt-1 font-body text-sm text-[#6B7280]">{s.desc}</p>
            </motion.div>
          ))}
          <div className="flex flex-col items-start justify-center rounded-3xl border-2 border-dashed border-[#E5DECF] bg-[#F2ECE1]/40 p-6">
            <div className="font-heading text-lg font-bold text-[#0D9488]">Now covering PreK through 8th</div>
            <p className="mt-1 font-body text-sm text-[#6B7280]">Reading, vocabulary, math and science lessons across every grade, PreK–8th.</p>
          </div>
        </div>
      </section>

      {/* Interactive engines */}
      <section id="engines" className="bg-[#1F2937] py-16 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Learning by doing</div>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Real interactive activities</h2>
            <p className="mx-auto mt-3 max-w-2xl font-body text-white/70">Not videos to watch — hands-on engines kids actually play with.</p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ENGINES.map((e, i) => (
              <motion.div key={e.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="rounded-3xl bg-white/5 p-6 ring-1 ring-white/10">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: e.color }}><e.icon size={24} /></div>
                <h3 className="mt-4 font-heading text-lg font-bold">{e.title}</h3>
                <p className="mt-1 font-body text-sm text-white/70">{e.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards */}
      <section id="rewards" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Motivation built in</div>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Points, stickers & badges kids love</h2>
            <p className="mt-3 font-body text-[#6B7280]">Every lesson rewards effort. Kids build a sticker book, earn points and unlock badges as they master each subject.</p>
            <div className="mt-6 space-y-4">
              {REWARDS.map((r) => (
                <div key={r.title} className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#F59E0B]/15 text-[#B45309]"><r.icon size={20} /></div>
                  <div><div className="font-heading font-bold">{r.title}</div><p className="font-body text-sm text-[#6B7280]">{r.text}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[["Reading Star", "#0284C7"], ["Word Wizard", "#7A3FF2"], ["Math Whiz", "#FF9E1B"], ["Science Explorer", "#10B981"]].map(([name, c]) => (
              <div key={name} className="flex flex-col items-center rounded-3xl border-2 bg-white p-6 text-center" style={{ borderColor: c }}>
                <div className="flex h-16 w-16 items-center justify-center rounded-full text-white" style={{ background: c }}><Star size={30} /></div>
                <div className="mt-3 font-heading text-sm font-bold">{name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-[#F2ECE1]/50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest font-bold text-[#F59E0B]">Simple, flexible plans</div>
            <h2 className="mt-2 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Start free — upgrade when you're ready</h2>
            <p className="mx-auto mt-3 max-w-2xl font-body text-[#6B7280]">For families, classrooms, whole schools, and reseller partners.</p>
          </div>
          <div className="mt-10"><PricingPlans /></div>
        </div>
      </section>

      {/* Trust */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: ShieldCheck, t: "Privacy-first", d: "No ads, no trackers. COPPA & FERPA aligned." },
            { icon: Heart, t: "Kind feedback", d: "Encouraging, never 'wrong' — kids stay confident." },
            { icon: Users, t: "Home & school", d: "Parents, teachers and school admins all supported." },
            { icon: GraduationCap, t: "Standards-minded", d: "Built around core PreK–8 skills and progressions." },
          ].map((x, i) => (
            <motion.div key={x.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="rounded-3xl border border-[#E5DECF] bg-white p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0D9488]/10 text-[#0D9488]"><x.icon size={22} /></div>
              <div className="mt-3 font-heading font-bold">{x.t}</div>
              <p className="mt-1 font-body text-sm text-[#6B7280]">{x.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="rounded-[32px] bg-gradient-to-r from-[#0D9488] to-[#7A3FF2] p-8 sm:p-12 text-center text-white">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Ready to light up learning?</h2>
          <p className="mt-2 font-body text-white/85">Create a free account and add your children or class in minutes.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/signup" className="inline-flex items-center gap-2 rounded-2xl bg-[#F59E0B] px-6 py-4 font-bold text-white shadow-md transition-all hover:bg-[#D97706] active:translate-y-0.5">Start Free <ArrowRight size={18} /></Link>
            <Link to="/login" className="inline-flex items-center gap-2 rounded-2xl bg-white/10 px-6 py-4 font-bold text-white ring-1 ring-white/30 transition-all hover:bg-white/20 active:translate-y-0.5">Log In</Link>
          </div>
        </div>
      </section>

      <footer className="bg-[#1F2937] py-10 text-center">
        <div className="mx-auto max-w-7xl px-4">
          <div className="font-heading text-lg font-extrabold text-white">ZoodLeap</div>
          <p className="mt-2 font-body text-sm text-white/60">The complete PreK–8th interactive learning platform · Reading · Vocabulary · Writing · Math · Science · Foreign Language · Social Communication · Social Studies · SEL · Life Skills</p>
          <p className="mt-4 font-body text-xs text-white/40">Privacy-first · COPPA & FERPA aligned · No ads</p>
        </div>
      </footer>
    </div>
  );
}
